import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Card } from "@/components/Shared";
import { RecordLayout, DetailPanel, Field } from "@/components/Record";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Truck, Loader2, PackageCheck, Send, Ban } from "lucide-react";

const STATUS = { Draft: "bg-[#EEF0F5] text-[#6B7189]", Sent: "bg-[#EAF3FF] text-[#1D4ED8]", "Partially Received": "bg-[#FEF3C7] text-[#B45309]", Received: "bg-[#DCFCE7] text-[#15803D]", Cancelled: "bg-[#FEE2E2] text-[#B91C1C]" };
const fdate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.is_super_admin || ["Owner", "Manager"].includes(user?.role);
  const [po, setPo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [recv, setRecv] = useState({});
  const load = useCallback(() => api.get(`/vendor-pos/${id}`).then((r) => setPo(r.data)).catch((e) => toast.error(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { setBusy(true); try { await fn(); toast.success("Done"); await load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const submit = () => act(() => api.post(`/vendor-pos/${id}/submit`));
  const cancel = () => { if (window.confirm("Cancel this purchase order?")) act(() => api.post(`/vendor-pos/${id}/cancel`, { reason: "Cancelled" })); };
  const startReceive = () => {
    const def = po.bins.find((b) => b.is_default)?.id || po.bins[0]?.id || null;
    const init = {}; po.lines.forEach((l) => { const pend = l.quantity - (l.received_qty || 0); if (pend > 0) init[l.id] = { quantity: pend, bin_id: def }; });
    setRecv(init); setReceiving(true);
  };
  const doReceive = () => {
    const payload = Object.entries(recv).filter(([, v]) => Number(v.quantity) > 0).map(([line_id, v]) => ({ line_id, quantity: Number(v.quantity), bin_id: v.bin_id }));
    if (payload.length === 0) { toast.error("Enter quantities to receive"); return; }
    act(async () => { await api.post(`/vendor-pos/${id}/receive`, { lines: payload }); setReceiving(false); });
  };
  if (!po) return <Loading />;
  return (
    <div data-testid="po-detail">
      <button onClick={() => navigate("/inventory?tab=purchasing")} className="flex items-center gap-1 text-sm text-[#101D51] mb-3" data-testid="po-back"><ChevronLeft className="w-4 h-4" /> Purchasing</button>
      <div className="bg-white border border-[#E6E8F0] rounded-xl px-4 sm:px-5 py-3 mb-4 shadow-sm flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-lg bg-[#101D51] flex items-center justify-center text-white shrink-0"><Truck className="w-5 h-5" /></div>
          <div className="min-w-0"><div className="text-xs text-[#6B7189]">Purchase order</div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#101D51] truncate">{po.number} <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 align-middle ${STATUS[po.status] || ""}`}>{po.status}</span></h1></div>
        </div>
        {isOwner && (
          <div className="flex flex-wrap gap-2">
            {po.status === "Draft" && <Button onClick={submit} disabled={busy} className="bg-[#101D51]" data-testid="po-submit"><Send className="w-4 h-4" /> Send PO</Button>}
            {["Sent", "Partially Received"].includes(po.status) && !receiving && <Button onClick={startReceive} className="bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="po-receive-start"><PackageCheck className="w-4 h-4" /> Receive goods</Button>}
            {receiving && <><Button variant="outline" onClick={() => setReceiving(false)}>Cancel</Button><Button onClick={doReceive} disabled={busy} className="bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="po-receive-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm receipt"}</Button></>}
            {!["Received", "Cancelled"].includes(po.status) && !receiving && <Button variant="outline" onClick={cancel} disabled={busy} data-testid="po-cancel"><Ban className="w-4 h-4" /> Cancel</Button>}
          </div>)}
      </div>
      <RecordLayout
        left={
          <DetailPanel title="Purchase order">
            <Field label="Number" value={po.number} />
            <Field label="Supplier" value={po.supplier?.name} />
            <Field label="Warehouse" value={po.warehouse?.name} />
            <Field label="Date" value={fdate(po.date)} />
            <Field label="Expected" value={po.expected_date ? fdate(po.expected_date) : "—"} />
            <Field label="Status" value={po.status} />
            <Field label="Total" value={formatINR(po.grand_total)} />
            <Field label="Notes" value={po.notes} />
          </DetailPanel>}
        right={
          <Card className="overflow-hidden" data-testid="po-lines">
            <div className="px-4 py-3 border-b border-[#E6E8F0] font-semibold text-[#101D51]">Line items ({po.lines.length})</div>
            <div className="divide-y divide-[#F0F1F5]">
              {po.lines.map((l) => {
                const pend = l.quantity - (l.received_qty || 0);
                return (
                  <div key={l.id} className="px-4 py-3" data-testid={`po-line-${l.code}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0"><div className="text-sm font-medium text-[#101D51] truncate">{l.name}</div><div className="text-[11px] text-[#6B7189]">{l.code} · {formatINR(l.unit_cost)}/{l.unit}</div></div>
                      <div className="text-right shrink-0 text-sm"><span className="font-semibold text-[#101D51]">{l.received_qty || 0}/{l.quantity}</span> <span className="text-[11px] text-[#6B7189]">{l.unit}</span></div>
                    </div>
                    {receiving && pend > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-24"><Label className="text-[10px] text-[#6B7189]">Receive</Label><Input type="number" min="0" max={pend} step="any" className="h-9 bg-white" value={recv[l.id]?.quantity ?? ""} onChange={(e) => setRecv({ ...recv, [l.id]: { ...recv[l.id], quantity: e.target.value } })} data-testid={`po-recv-qty-${l.code}`} /></div>
                        <div className="flex-1"><Label className="text-[10px] text-[#6B7189]">Into bin</Label>
                          <Select value={recv[l.id]?.bin_id || ""} onValueChange={(v) => setRecv({ ...recv, [l.id]: { ...recv[l.id], bin_id: v } })}>
                            <SelectTrigger className="h-9 bg-white" data-testid={`po-recv-bin-${l.code}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{po.bins.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}</SelectContent>
                          </Select></div>
                      </div>)}
                  </div>);
              })}
              <div className="px-4 py-3 text-right text-sm font-semibold text-[#101D51]">Total: {formatINR(po.grand_total)}</div>
            </div>
          </Card>} />
    </div>);
}
